import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
    startOfMonth,
    endOfMonth,
    addDays,
    format,
    isSameDay,
    addMonths,
    startOfWeek,
    endOfWeek,
    addWeeks,
    differenceInCalendarDays,
    sub,
} from "date-fns";
import { FaChevronLeft, FaChevronRight, FaHome, FaUsers } from "react-icons/fa";
// import ManagerNavbar from "../Layout/AdminNavbar";
import axios from "axios";
import API_BASE_URL from "../config";
import "./Timesheet.css";
import AdminNavbar from "../Layout/AdminNavbar";

const AllTimesheetGrid = () => {
    // const { managerId } = useParams();
    const managerId = localStorage.getItem("employeeId");

    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    //const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

    const [worklogs, setWorklogs] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [viewMode, setViewMode] = useState("monthly");
    const [billableFilter, setBillableFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
    const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
    const [error, setError] = useState("");
    const [holidayDates, setHolidayDates] = useState([]);
    const [holidayMap, setHolidayMap] = useState({});
    const [weeklyHolidays, setWeeklyHolidays] = useState([]);
    const invalidDateToastShown = useRef(false);
    const [leaveDates, setLeaveDates] = useState([]);
    const employeeDropdownRef = useRef(null);
    const [financialYear, setFinancialYear] = useState("");


    const token = localStorage.getItem("token");
    const employeeId = localStorage.getItem("employeeId"); // acts as reporter/manager id

    const showBillable = billableFilter === "all" || billableFilter === "billable";
    const showNonBillable = billableFilter === "all" || billableFilter === "non-billable";

    const sortedEmployees = [...employees].sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`,
            undefined,
            { sensitivity: "base" }
        )
    );



    const visibleEmployees =
        selectedEmployees.length > 0
            ? [...selectedEmployees].sort((a, b) =>
                `${a.firstName} ${a.lastName}`.localeCompare(
                    `${b.firstName} ${b.lastName}`,
                    undefined,
                    { sensitivity: "base" }
                )
            )
            : sortedEmployees;





    const formatHours = (value) =>
        Number.isFinite(value) ? value.toFixed(2) : "0.00";


    const round2 = (num) =>
        Math.round((num + Number.EPSILON) * 100) / 100;


    useEffect(() => {
        const token = localStorage.getItem("token");
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");

        if (!token) {
            toast.error("You must be logged in to access this page.");
            navigate("/");
            return;
        }

        //  check admin role
        if (!roles.includes("ROLE_ADMIN")) {
            toast.error("You are not authorized to access this page.");
            navigate("/");
            return;
        }
    }, [navigate]);


    useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(res => res.json())
      .then(data => {


        // API returns array → take first record
        const company = Array.isArray(data) ? data[0] : data;

        if (!company?.financialYear) {
          console.warn("No financialYear found");
          return;
        }

        setFinancialYear(company.financialYear);
      })

      .catch(err => console.error("Error fetching company info:", err));
  }, []);


    useEffect(() => {
        if (!token || !employeeId) {
            console.error("Missing token or employeeId");
            navigate("/");
            return;
        }

        const fetchEmployees = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/employees/active`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const data = res.data || [];
                setEmployees(data);

                //  IMPORTANT:
                // Do NOT auto-select anyone
                // Empty selectedEmployees = show ALL employees
                setSelectedEmployees([]);

            } catch (err) {
                console.error("Failed to fetch employees:", err);
                setError("Unable to load your subordinates.");
            }
        };

        fetchEmployees();
    }, [token, employeeId, navigate]);


    const isEmployeeSelected = (empId) =>
        selectedEmployees.some(e => e.id === empId);


    // Load Bootstrap dynamically
    useEffect(() => {
        // Bootstrap CSS
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        bootstrapLink.id = "timesheetgrid-bootstrap";
        document.head.appendChild(bootstrapLink);

        // Bootstrap Icons CSS
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        iconsLink.id = "timesheetgrid-icons";
        document.head.appendChild(iconsLink);

        return () => {
            const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
            if (existingBootstrap) existingBootstrap.remove();

            const existingIcons = document.getElementById("timesheetgrid-icons");
            if (existingIcons) existingIcons.remove();
        };
    }, []);


    useEffect(() => {
  const fetchWeeklyHolidays = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/holidays/weekly`,
        {
          headers: {
            Authorization: `Bearer ${token}`, 
          },
        }
      );

      const days = res.data.map(h => h.name); 
      setWeeklyHolidays(days);
    } catch (err) {
      console.error("Failed to fetch weekly holidays:", err);
    }
  };

  fetchWeeklyHolidays();
}, [token]); //  include token



    useEffect(() => {
  const fetchHolidays = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/holidays`,
        {
          headers: {
            Authorization: `Bearer ${token}`, 
          },
        }
      );

      const holidays = res.data || [];

      const dates = [];
      const map = {};

      holidays.forEach((h) => {
        const start = new Date(h.startDate);
        const end = new Date(h.endDate);
        const daysBetween =
          (end - start) / (1000 * 60 * 60 * 24) + 1;

        for (let i = 0; i < daysBetween; i++) {
          const d = new Date(start);
          d.setDate(start.getDate() + i);
          const formatted = format(d, "yyyy-MM-dd");
          dates.push(formatted);
          map[formatted] = h.name;
        }
      });

      setHolidayDates(dates);
      setHolidayMap(map);
    } catch (err) {
      console.error("Failed to fetch holidays:", err);
    }
  };

  fetchHolidays();
}, [token]); 



    useEffect(() => {
        const fetchWorklogs = async (showSpinner = false) => {
            if (showSpinner) setLoading(true);

            try {
                const res = await axios.get(
                    `${API_BASE_URL}/api/worklogs`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const data = Array.isArray(res.data) ? res.data : [];

                if (selectedEmployees.length > 0) {
                    const ids = selectedEmployees.map(e => e.id);
                    setWorklogs(data.filter(w => ids.includes(w.employeeId)));
                } else {
                    setWorklogs(data);
                }
            } catch (err) {
                console.error(err);
                setError("Unable to load worklogs");
            } finally {
                if (showSpinner) setLoading(false);
            }
        };


        fetchWorklogs(worklogs.length === 0);

    }, [selectedEmployees]);




    



    useEffect(() => {
  const fetchLeaves = async () => {
    const token = localStorage.getItem("token");

    if (!token || !financialYear) {
      setLeaveDates([]);
      return;
    }

    let leaveList = [];

    const processLeaves = (leaves, empId) => {
      leaves
        .filter(lv => lv.leaveStatus === "APPROVED") //  AV status
        .forEach(lv => {
          const start = new Date(lv.startDate);
          const end = new Date(lv.endDate);

          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            leaveList.push({
              employeeId: empId,
              date: d.toISOString().split("T")[0],
            });
          }
        });
    };

    const targetEmployees =
      selectedEmployees.length > 0 ? selectedEmployees : employees;

    for (let emp of targetEmployees) {
      const res = await axios.get(
        `${API_BASE_URL}/api/leave-transactions/employee/${emp.id}/avail`,
        {
          params: { financialYear },          //  required
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      processLeaves(res.data, emp.id);
    }

    setLeaveDates(leaveList);
  };

  fetchLeaves();
}, [selectedEmployees, employees, financialYear]);



    const isLeaveForEmployee = (employeeId, day) => {
        const formatted = format(day, "yyyy-MM-dd");
        return leaveDates.some(ld => ld.employeeId === employeeId && ld.date === formatted);
    };


    const isWeeklyHoliday = (day) => {
        const dayName = format(day, "EEEE"); // e.g., "Monday"
        return weeklyHolidays.includes(dayName);
    };


    // const handleExport = async (fileFormat) => {
    //     if (!selectedEmployee) return;
    //     const token = localStorage.getItem("token");
    //     if (!token) return;

    //     try {
    //         // Format dates as yyyy-MM-dd
    //         const fromDateStr = startDate ? startDate.toISOString().split("T")[0] : "";
    //         const toDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

    //         // Build URL params
    //         const params = new URLSearchParams();
    //         params.append("type", "date"); // must be "date" for fromDate/toDate filter
    //         if (fromDateStr) params.append("fromDate", fromDateStr);
    //         if (toDateStr) params.append("toDate", toDateStr);

    //         // Add billing type separately as a query param if needed
    //         if (billableFilter && billableFilter !== "all") params.append("billingType", billableFilter);

    //         const response = await axios.get(
    //             `${API_BASE_URL}/api/reports/timesheet/${selectedEmployee.id}?${params.toString()}&format=${fileFormat}`,
    //             {
    //                 headers: { Authorization: `Bearer ${token}` },
    //                 responseType: "blob",
    //             }
    //         );

    //         const blob = new Blob([response.data], {
    //             type:
    //                 fileFormat === "pdf"
    //                     ? "application/pdf"
    //                     : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    //         });

    //         const url = window.URL.createObjectURL(blob);
    //         const link = document.createElement("a");
    //         link.href = url;
    //         link.setAttribute(
    //             "download",
    //             `Timesheet_${selectedEmployee.firstName}_${selectedEmployee.lastName}.${fileFormat === "pdf" ? "pdf" : "xlsx"
    //             }`
    //         );
    //         document.body.appendChild(link);
    //         link.click();
    //         link.remove();
    //     } catch (err) {
    //         console.error("Export failed:", err);
    //         alert("Failed to export report. Please try again.");
    //     }
    // };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                employeeDropdownRef.current &&
                !employeeDropdownRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    // Generate dynamic days
    const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
    const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

    const handlePrev = () => {
        if (viewMode === "monthly") {
            const newStart = sub(startDate, { months: 1 });
            setStartDate(newStart);
            setEndDate(endOfMonth(newStart));
        } else {
            const newStart = sub(startDate, { weeks: 1 });
            setStartDate(newStart);
            setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
        }
    };

    const handleNext = () => {
        if (viewMode === "monthly") {
            const newStart = addMonths(startDate, 1);
            setStartDate(newStart);
            setEndDate(endOfMonth(newStart));
        } else {
            const newStart = addWeeks(startDate, 1);
            setStartDate(newStart);
            setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
        }
    };

    const filteredWorklogs = (() => {
        if (!startDate || !endDate) return [];

        if (endDate < startDate) {
            if (!invalidDateToastShown.current) {
                toast.error("End date must be greater than or equal to Start date.");
                invalidDateToastShown.current = true;
            }
            return [];
        } else {
            invalidDateToastShown.current = false; // reset when valid
        }

        return worklogs.filter((log) => {
            const logDate = new Date(log.date);
            const inDateRange = logDate >= startDate && logDate <= endDate;
            if (!inDateRange) return false;

            if (billableFilter === "all") return true;
            return log.billingType === billableFilter;
        });
    })();


    // Row total for each employee
    const getRowTotal = (employeeId) =>
        round2(days.reduce((sum, day) => sum + getHours(employeeId, day), 0));


    // Column total for each day & type
    const getColumnTotal = (day, type) =>
        round2(
            visibleEmployees.reduce((sum, emp) => sum + getHours(emp.id, day, type), 0)
        );



    const getHours = (employeeId, day, billableType) =>
        filteredWorklogs
            .filter(
                log =>
                    log.employeeId === employeeId &&
                    isSameDay(new Date(log.date), day) &&
                    (!billableType || log.billingType === billableType)
            )
            .reduce((sum, log) => sum + Number(log.hours), 0);




    const totalHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
    const totalBillable = filteredWorklogs
        .filter((log) => log.billingType === "billable")
        .reduce((sum, log) => sum + log.hours, 0);
    const totalNonBillable = totalHours - totalBillable;

    const totalFilteredHours = round2(
        filteredWorklogs.reduce((sum, log) => sum + log.hours, 0)
    );

    const totalFilteredBillable = round2(
        filteredWorklogs
            .filter((log) => log.billingType === "billable")
            .reduce((sum, log) => sum + log.hours, 0)
    );

    const totalFilteredNonBillable = round2(
        filteredWorklogs
            .filter((log) => log.billingType === "non-billable")
            .reduce((sum, log) => sum + log.hours, 0)
    );


    if (loading)
        return (
            <div className="d-flex justify-content-center align-items-center mt-5">
                <div className="spinner-border text-primary"></div>
                <span className="ms-2 fs-5">Loading timesheet...</span>
            </div>
        );

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="fixed-top">
                <AdminNavbar />
            </div>
            <div className="container-fluid mt-4" style={{ paddingTop: "110px" }}>
                {/* Header */}
                <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-white rounded shadow-sm border gap-3">
                    {/* Employee Selector */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <FaUsers className="text-primary fs-4" />
                        <div className="position-relative" ref={employeeDropdownRef}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowEmployeeDropdown(prev => !prev);
                                }}
                            >

                                <FaUsers />
                                Employees
                                {/* {selectedEmployees.length > 0 && (
                                    <span className="badge bg-primary">
                                        {selectedEmployees.length}
                                    </span>
                                )} */}
                            </button>

                            {showEmployeeDropdown && (
                                <div
                                    className="assignee-dropdown"
                                    style={{
                                        width: "260px",
                                        maxHeight: "280px",
                                        overflowY: "auto"
                                    }}
                                >


                                    {/* ALL EMPLOYEES OPTION */}
                                    <div
                                        className={`assignee-dropdown-item ${selectedEmployees.length === 0 ? "active" : ""
                                            }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSelectedEmployees([]);
                                        }}

                                    >
                                        <div className="task-card-emp__avatar bg-light text-dark">
                                            A
                                        </div>
                                        <span>All Employees</span>
                                    </div>

                                    {/* EMPLOYEE LIST */}
                                    {sortedEmployees.map(emp => {
                                        const isSelected = isEmployeeSelected(emp.id);

                                        return (
                                            <div
                                                key={emp.id}
                                                className={`assignee-dropdown-item ${isSelected ? "active" : ""}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setSelectedEmployees(prev =>
                                                        isSelected
                                                            ? prev.filter(x => x.id !== emp.id)
                                                            : [...prev, emp]
                                                    );
                                                }}

                                            >
                                                <div className="task-card-emp__avatar">
                                                    {emp.photoPath ? (
                                                        <img
                                                            src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
                                                            onError={(e) => (e.currentTarget.src = "/default-avatar.png")}
                                                        />
                                                    ) : (
                                                        emp.firstName?.charAt(0)?.toUpperCase()
                                                    )}
                                                </div>

                                                <span>
                                                    {emp.firstName} {emp.lastName}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                        </div>

                    </div>

                    {/* Controls */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            onClick={() => navigate(-1)}
                        >
                            <FaChevronLeft /> Back
                        </button>
                        {/* <button
                            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            onClick={() => navigate("/managerdashboard")}
                        >
                            <FaHome /> Home
                        </button> */}

                        <button
                            className={`btn btn-sm ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                setViewMode("monthly");
                                setStartDate(startOfMonth(startDate));
                                setEndDate(endOfMonth(startDate));
                            }}
                        >
                            Monthly
                        </button>
                        <button
                            className={`btn btn-sm ${viewMode === "weekly" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                const today = new Date(); // Get today's date
                                setViewMode("weekly");
                                setStartDate(startOfWeek(today, { weekStartsOn: 1 })); // Monday as start of week
                                setEndDate(endOfWeek(today, { weekStartsOn: 1 }));     // Sunday as end of week
                            }}
                        >
                            Weekly
                        </button>

                        <div className="btn-group btn-group-sm">
                            <button
                                className={`btn btn-sm ${billableFilter === "all" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("all")}
                            >
                                All
                            </button>
                            <button
                                className={`btn btn-sm ${billableFilter === "billable" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("billable")}
                            >
                                Billable
                            </button>
                            <button
                                className={`btn btn-sm ${billableFilter === "non-billable" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("non-billable")}
                            >
                                Non-Billable
                            </button>
                        </div>

                        <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={handlePrev}>
                                <FaChevronLeft />
                            </button>
                            <button className="btn btn-outline-primary" onClick={handleNext}>
                                <FaChevronRight />
                            </button>
                        </div>
                        {/* Date Range */}
                        <div className="d-flex gap-1 align-items-center">
                            <input
                                type="date"
                                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                                onChange={(e) =>
                                    setStartDate(e.target.value ? new Date(e.target.value) : null)
                                }
                                className="form-control form-control-sm"
                                style={{ width: "110px" }}
                            />
                            <span className="text-muted">to</span>
                            <input
                                type="date"
                                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                                onChange={(e) =>
                                    setEndDate(e.target.value ? new Date(e.target.value) : null)
                                }
                                className="form-control form-control-sm"
                                style={{ width: "110px" }}
                            />
                        </div>
                        {/* Export Buttons */}
                        {/* <div className="btn-group btn-group-sm ms-2">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleExport("pdf")}
                        >
                            Export PDF
                        </button>
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleExport("excel")}
                        >
                            Export Excel
                        </button>
                    </div> */}

                    </div>
                </div>

                {/* Compact Summary Cards */}
                <div className="row g-2 mb-3 mt-2">
                    {[
                        { title: "Total Hours", value: formatHours(totalFilteredHours), bg: "primary", icon: "bi-hourglass-split" },
                        { title: "Billable", value: formatHours(totalFilteredBillable), bg: "success", icon: "bi-cash-coin" },
                        { title: "Non-Billable", value: formatHours(totalFilteredNonBillable), bg: "danger", icon: "bi-slash-circle" },
                    ]
                        .map((card, idx) => (
                            <div key={idx} className="col-12 col-sm-4 col-md-2">
                                <div className="card shadow-sm border-0 h-100 p-2">
                                    <div className="d-flex align-items-center gap-2">
                                        {/* Icon */}
                                        <div
                                            className={`rounded-circle bg-${card.bg} bg-opacity-10 text-${card.bg} d-flex align-items-center justify-content-center`}
                                            style={{ width: "35px", height: "35px" }}
                                        >
                                            <i className={`bi ${card.icon} fs-6`}></i>
                                        </div>

                                        {/* Text */}
                                        <div className="flex-grow-1">
                                            <h6 className="mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                                                {card.title}
                                            </h6>
                                            <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                {card.value} hrs
                                            </small>
                                        </div>

                                        {/* Value */}
                                        <h6 className={`mb-0 text-${card.bg} fw-bold`} style={{ fontSize: "0.9rem" }}>
                                            {card.value}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>


                {/* Timesheet Grid */}
                <div
                    className="shadow-none rounded border mt-3"
                    style={{
                        display: "flex",
                        width: "100%",
                        maxHeight: "65vh",
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            minWidth: "100%",
                            overflowX: "auto",
                        }}
                    >


                        {/* Left Sticky Column */}
                        <div
                            style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 30,
                                background: "white",
                                flex: "0 0 180px",
                                width: "180px",
                            }}
                        >


                            <table
                                className="table table-bordered mb-0 text-center"
                                style={{
                                    width: "180px",
                                    tableLayout: "fixed",
                                }}
                            >

                                <thead className="table-primary">
                                    <tr>
                                        <th
                                            rowSpan={2}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 20,
                                                background: "#e9f2ff",
                                                height: "106px",          // 65 + 65
                                            }}
                                        >
                                            Employee
                                        </th>
                                    </tr>
                                </thead>


                                <tbody>
                                    {/* Top empty row for alignment */}
                                    {/* <tr><span style={{ visibility: "hidden" }}>0</span></tr> */}

                                    {visibleEmployees.map((emp) => (
                                        <tr key={emp.id}>
                                            <td
                                                className="text-start fw-normal"
                                                style={{ width: "180px" }}
                                            >
                                                {emp.firstName} {emp.lastName}</td>
                                        </tr>
                                    ))}

                                    {/* Bottom empty row for alignment */}
                                    {/* <tr><td>&nbsp;</td></tr> */}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td
                                            style={{
                                                position: "sticky",
                                                bottom: 0,
                                                zIndex: 15,
                                                background: "#f8f9fa",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            Daily Total
                                        </td>
                                    </tr>
                                </tfoot>

                            </table>
                        </div>




                        {/* Middle Scrollable Section */}
                        <div style={{ flex: 1 }}>
                            <table className="table table-bordered mb-0 text-center" style={{ minWidth: days.length * 100, tableLayout: "fixed" }}>
                                <thead className="table-primary">
                                    <tr>
                                        {days.map((day, i) => {
                                            const formatted = format(day, "yyyy-MM-dd");
                                            const isHoliday = holidayDates.includes(formatted);
                                            const isWeekly = isWeeklyHoliday(day);

                                            const bgColor = isHoliday ? "#d4f8d4" : isWeekly ? "#f2f2f2" : "";
                                            const borderColor = "#e0e0e0";
                                            const tooltip = isHoliday
                                                ? holidayMap[formatted]
                                                : isWeekly
                                                    ? "Weekly Holiday"
                                                    : "";

                                            return (
                                                <th
                                                    key={i}
                                                    colSpan={(showBillable ? 1 : 0) + (showNonBillable ? 1 : 0)}
                                                    title={tooltip}
                                                    style={{
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 20,
                                                        background: bgColor || "#e9f2ff",
                                                        minWidth: showBillable && showNonBillable ? "140px" : "70px",
                                                        borderColor,
                                                    }}
                                                >
                                                    {format(day, "d")}
                                                    <br />
                                                    <small className={isHoliday ? "text-secondary fw-semibold" : "text-muted"}>
                                                        {format(day, "EEE")}
                                                    </small>
                                                </th>
                                            );
                                        })}
                                    </tr>

                                    <tr>
                                        {days.map((day, i) => {
                                            const formatted = format(day, "yyyy-MM-dd");
                                            const isHoliday = holidayDates.includes(formatted);
                                            const isWeekly = isWeeklyHoliday(day);

                                            const bgColor = isHoliday ? "#d4f8d4" : isWeekly ? "#f2f2f2" : "";
                                            const borderColor = "#e0e0e0";
                                            const tooltip = isHoliday
                                                ? holidayMap[formatted]
                                                : isWeekly
                                                    ? "Weekly Holiday"
                                                    : "";

                                            return (
                                                <React.Fragment key={i}>
                                                    {showBillable && (
                                                        <th
                                                            title={tooltip}
                                                            style={{
                                                                position: "sticky",
                                                                top: 65,
                                                                zIndex: 19,
                                                                background: bgColor || "#e9f2ff",
                                                                width: "60px",
                                                                borderColor,
                                                                fontWeight: "normal",
                                                            }}
                                                        >
                                                            BL
                                                        </th>
                                                    )}

                                                    {showNonBillable && (
                                                        <th
                                                            title={tooltip}
                                                            style={{
                                                                position: "sticky",
                                                                top: 65,
                                                                zIndex: 19,
                                                                background: bgColor || "#e9f2ff",
                                                                width: "70px",
                                                                borderColor,
                                                                fontWeight: "normal",
                                                            }}
                                                        >
                                                            NBL
                                                        </th>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                </thead>

                                <tbody>
                                    {visibleEmployees.map((emp) => (
                                        <tr key={emp.id}>
                                            {days.map((day, j) => {
                                                const formatted = format(day, "yyyy-MM-dd");
                                                const isHoliday = holidayDates.includes(formatted);
                                                const isWeekly = isWeeklyHoliday(day);

                                                const logs = filteredWorklogs.filter(
                                                    (log) => log.employeeId === emp.id && isSameDay(new Date(log.date), day)
                                                );

                                                const billable = logs
                                                    .filter((l) => l.billingType === "billable")
                                                    .reduce((sum, l) => sum + l.hours, 0);
                                                const nonBillable = logs
                                                    .filter((l) => l.billingType === "non-billable")
                                                    .reduce((sum, l) => sum + l.hours, 0);

                                                const cellStyle = {
                                                    backgroundColor: isLeaveForEmployee(emp.id, day) ? "#fff0f0" : isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : "",
                                                    borderColor: isHoliday ? "#e0e0e0" : isWeekly ? "#e0e0e0" : "",
                                                };

                                                const tooltip = isHoliday
                                                    ? holidayMap[formatted]
                                                    : isWeekly
                                                        ? "Weekly Holiday"
                                                        : "";

                                                return (
                                                    <React.Fragment key={j}>
                                                        {showBillable && (
                                                            <td
                                                                style={{ ...cellStyle, width: "70px" }}
                                                                title={tooltip}
                                                            >
                                                                {billable
                                                                    ? round2(billable).toFixed(2)
                                                                    : <span style={{ visibility: "hidden" }}>0</span>}

                                                            </td>
                                                        )}

                                                        {showNonBillable && (
                                                            <td
                                                                style={{ ...cellStyle, width: "70px" }}
                                                                title={tooltip}
                                                            >
                                                                {nonBillable
                                                                    ? round2(nonBillable).toFixed(2)
                                                                    : <span style={{ visibility: "hidden" }}>0</span>}

                                                            </td>
                                                        )}

                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        {days.map((day, i) => {
                                            const formatted = format(day, "yyyy-MM-dd");
                                            const isHoliday = holidayDates.includes(formatted);
                                            const isWeekly = isWeeklyHoliday(day);

                                            const bgColor = isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : "#f8f9fa";
                                            const borderColor = "#e0e0e0";
                                            const tooltip = isHoliday
                                                ? holidayMap[formatted]
                                                : isWeekly
                                                    ? "Weekly Holiday"
                                                    : "";

                                            return (
                                                <React.Fragment key={i}>
                                                    {showBillable && (
                                                        <td
                                                            title={tooltip}
                                                            style={{
                                                                position: "sticky",
                                                                bottom: 0,
                                                                zIndex: 15,
                                                                background: bgColor,
                                                                borderColor,
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {getColumnTotal(day, "billable") || (
                                                                <span style={{ visibility: "hidden" }}>0</span>
                                                            )}
                                                        </td>
                                                    )}

                                                    {showNonBillable && (
                                                        <td
                                                            title={tooltip}
                                                            style={{
                                                                position: "sticky",
                                                                bottom: 0,
                                                                zIndex: 15,
                                                                background: bgColor,
                                                                borderColor,
                                                                fontWeight: "bold",
                                                            }}
                                                        >
                                                            {getColumnTotal(day, "non-billable") || (
                                                                <span style={{ visibility: "hidden" }}>0</span>
                                                            )}
                                                        </td>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                </tfoot>


                            </table>
                        </div>
                        {/* Right Sticky Column (Billable / Non-Billable / Total) */}
                        <div
                            style={{
                                position: "sticky",
                                right: 0,
                                zIndex: 30,
                                background: "white",
                                flex: "0 0 210px",
                                width: "210px",
                            }}
                        >


                            <table className="table table-bordered mb-0 text-center">
                                <thead className="table-primary">
                                    <tr>
                                        <th
                                            colSpan={3}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 20,
                                                background: "#e9f2ff",
                                                minWidth: "210px",
                                                height: "65px",
                                            }}
                                        >
                                            Total
                                        </th>
                                    </tr>

                                    <tr>
                                        {showBillable && (
                                            <th
                                                style={{
                                                    position: "sticky",
                                                    top: 65,
                                                    zIndex: 19,
                                                    background: "#e9f2ff",
                                                    fontWeight: "normal",
                                                }}
                                            >
                                                BL
                                            </th>
                                        )}

                                        {showNonBillable && (
                                            <th
                                                style={{
                                                    position: "sticky",
                                                    top: 65,
                                                    zIndex: 19,
                                                    background: "#e9f2ff",
                                                    fontWeight: "normal",
                                                }}
                                            >
                                                NBL
                                            </th>
                                        )}

                                        <th
                                            style={{
                                                position: "sticky",
                                                top: 65,
                                                zIndex: 19,
                                                background: "#e9f2ff",
                                                fontWeight: "normal",
                                            }}
                                        >
                                            Total
                                        </th>
                                    </tr>
                                </thead>


                                <tbody>
                                    {visibleEmployees.map((emp) => {
                                        const totalBillable = round2(
                                            days.reduce(
                                                (sum, day) => sum + getHours(emp.id, day, "billable"),
                                                0
                                            )
                                        );

                                        const totalNonBillable = round2(
                                            days.reduce(
                                                (sum, day) => sum + getHours(emp.id, day, "non-billable"),
                                                0
                                            )
                                        );

                                        const total = round2(totalBillable + totalNonBillable);

                                        return (
                                            <tr key={emp.id}>
                                                {showBillable && (
                                                    <td className="fw-semibold text-success">
                                                        {totalBillable.toFixed(2)}
                                                    </td>
                                                )}

                                                {showNonBillable && (
                                                    <td className="fw-semibold text-danger">
                                                        {totalNonBillable.toFixed(2)}
                                                    </td>
                                                )}

                                                <td className="fw-semibold text-primary">
                                                    {total.toFixed(2)}
                                                </td>
                                            </tr>

                                        );
                                    })}
                                </tbody>


                                <tfoot>
                                    <tr>
                                        {showBillable && (
                                            <td
                                                style={{
                                                    position: "sticky",
                                                    bottom: 0,
                                                    zIndex: 15,
                                                    background: "#f8f9fa",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {round2(
                                                    visibleEmployees.reduce(
                                                        (sum, emp) =>
                                                            sum +
                                                            days.reduce(
                                                                (daySum, day) =>
                                                                    daySum + getHours(emp.id, day, "billable"),
                                                                0
                                                            ),
                                                        0
                                                    )
                                                )}
                                            </td>
                                        )}

                                        {showNonBillable && (
                                            <td
                                                style={{
                                                    position: "sticky",
                                                    bottom: 0,
                                                    zIndex: 15,
                                                    background: "#f8f9fa",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {round2(
                                                    visibleEmployees.reduce(
                                                        (sum, emp) =>
                                                            sum +
                                                            days.reduce(
                                                                (daySum, day) =>
                                                                    daySum + getHours(emp.id, day, "non-billable"),
                                                                0
                                                            ),
                                                        0
                                                    )
                                                )}
                                            </td>
                                        )}

                                        <td
                                            style={{
                                                position: "sticky",
                                                bottom: 0,
                                                zIndex: 15,
                                                background: "#f8f9fa",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {round2(
                                                visibleEmployees.reduce(
                                                    (sum, emp) =>
                                                        sum +
                                                        days.reduce(
                                                            (daySum, day) => daySum + getHours(emp.id, day),
                                                            0
                                                        ),
                                                    0
                                                )
                                            )}
                                        </td>
                                    </tr>
                                </tfoot>

                            </table>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default AllTimesheetGrid;
